import { localizeDimensionSlug } from '../best-companies/dimension-slugs';
import { DEFAULT_SEO_TITLE_MAX_LENGTH, truncateSeoTitle } from './truncate-seo-title';
import { clampMetaTitle } from './clamp-meta-title';
import type { DimensionConfig } from '../../pages/best-companies/dimensionConfig';
import {
  getBestCompaniesDimensionDisplayLabel,
  getBestCompaniesIndustryScopePhrase,
  getBestCompaniesIndustrySeoName,
  getBestCompaniesRegionFiForms,
} from './best-companies-dimension-labels';
import { getCityGenitiveOrNull, getCityInessiveOrNull } from './generate-srp-seo-meta';
import { localizeRoute } from '../constants/url-constants';

export const BEST_COMPANIES_SUPPORTED_LOCALES = ['en', 'fi', 'sv'] as const;
export type BestCompaniesLocale = (typeof BEST_COMPANIES_SUPPORTED_LOCALES)[number];

export function isBestCompaniesLocale(value: string): value is BestCompaniesLocale {
  return (BEST_COMPANIES_SUPPORTED_LOCALES as readonly string[]).includes(value);
}

export function normalizeBestCompaniesLocale(value: string | undefined): BestCompaniesLocale {
  if (value && isBestCompaniesLocale(value)) return value;
  return 'en';
}

export function inferBestCompaniesLocaleFromPathname(pathname: string): BestCompaniesLocale {
  const segments = pathname.split('/').filter(Boolean);
  const first = segments[0];
  return isBestCompaniesLocale(first) ? first : 'en';
}

/** Calendar year for rankings copy (title, H1, freshness). */
export function getBestCompaniesHubRankingYear(referenceDate: Date = new Date()): number {
  return referenceDate.getFullYear();
}

/**
 * Clamp meta description (~160 chars) at a word boundary when possible.
 */
export function clampMetaDescription(text: string, maxLength = 160): string {
  const t = text.trim();
  if (t.length <= maxLength) return t;
  let cut = t.slice(0, maxLength);

  // Don't let the cut land inside an unclosed "(...)" clause (e.g. a long industry label):
  // that reads as a broken sentence in a SERP snippet, e.g. "...(Sähkömoottorien, generaattorien
  // ja…" with the closing paren and the rest of the sentence gone. Back the cut up to right
  // before that clause starts instead of word-wrapping inside it.
  const openParens = (cut.match(/\(/g) || []).length;
  const closeParens = (cut.match(/\)/g) || []).length;
  if (openParens > closeParens) {
    const lastOpenIdx = cut.lastIndexOf('(');
    if (lastOpenIdx > 0) {
      cut = cut.slice(0, lastOpenIdx);
    }
  }

  const lastSpace = cut.lastIndexOf(' ');
  if (lastSpace > Math.min(100, maxLength - 20)) {
    return `${cut.slice(0, lastSpace).trim()}…`;
  }
  return `${cut.trim()}…`;
}

/** English logical path (Vike `urlLogical`). */
export function bestCompaniesPath(dimensionSlug?: string): string {
  return dimensionSlug ? `/best-companies/${dimensionSlug}` : '/best-companies';
}

/**
 * Public URL path including locale prefix and **localized** hub segment
 * (EN `employers`, FI `tyonantajat`, SV `alla-arbetsgivare`); use for canonical, hreflang, sitemap.
 */
export function bestCompaniesCanonicalPath(locale: BestCompaniesLocale, dimensionSlug?: string): string {
  // `dimensionSlug` is the dimension key; fi/sv industry and region pages get their own slug.
  const logical = bestCompaniesPath(dimensionSlug ? localizeDimensionSlug(dimensionSlug, locale) : undefined);
  return `/${locale}${localizeRoute(logical, locale)}`;
}

/** clampMetaTitle, but never leaves a dangling " |" when the brand suffix is clipped off. */
function clampTitleClean(title: string): string {
  return clampMetaTitle(title).replace(/\s*[|:]\s*$/, '').trim();
}

export function buildBestCompaniesHubHeading(locale: BestCompaniesLocale): string {
  if (locale === 'fi') return 'Suomen suurimmat työnantajat';
  if (locale === 'sv') return 'Största arbetsgivarna i Finland';
  return 'Companies in Finland';
}

export function buildBestCompaniesHubSeo(
  locale: BestCompaniesLocale,
  options?: { year?: number; hiringCompanyCount?: number | null },
): {
  title: string;
  description: string;
  keywords: string;
  ogLocale: string;
} {
  const count = options?.hiringCompanyCount && options.hiringCompanyCount > 0 ? options.hiringCompanyCount : null;
  // Targets the head terms searchers actually use ("companies in Finland", "suomen suurimmat
  // työnantajat", "största arbetsgivarna i Finland"); the Employer Score is a sort and a column,
  // not a claim in the title. Count clause first, so the 60-char clamp sheds it, not the brand.
  if (locale === 'fi') {
    const n = count ? formatCountForLocale(count, 'fi') : null;
    return {
      title: clampTitleClean(
        n
          ? `Suomen suurimmat työnantajat: ${n} rekrytoi nyt | JobCrawls`
          : 'Suomen suurimmat työnantajat ja ketkä rekrytoivat | JobCrawls',
      ),
      description: clampMetaDescription(
        'Suomen suurimmat työnantajat henkilöstön mukaan ja yritykset, jotka rekrytoivat nyt: avoimet työpaikat, palkkahaarukat ja etätyön osuus. Selaa kaupungeittain.',
      ),
      keywords:
        'suomen suurimmat työnantajat, suurimmat yritykset henkilöstön mukaan, rekrytoivat yritykset, työnantajat Suomessa, avoimet työpaikat yrityksittäin, JobCrawls',
      ogLocale: 'fi_FI',
    };
  }
  if (locale === 'sv') {
    const n = count ? formatCountForLocale(count, 'sv') : null;
    return {
      title: clampTitleClean(
        n
          ? `Största arbetsgivarna i Finland: ${n} rekryterar nu | JobCrawls`
          : 'Största arbetsgivarna i Finland och vem som rekryterar | JobCrawls',
      ),
      description: clampMetaDescription(
        'De största arbetsgivarna i Finland efter antal anställda och företag i Finland som rekryterar nu: lediga jobb, lönespann och andel distansarbete. Bläddra per stad.',
      ),
      keywords:
        'största arbetsgivarna i Finland, företag i Finland, arbetsgivare Finland, företag som rekryterar, lediga jobb per företag, JobCrawls',
      ogLocale: 'sv_SE',
    };
  }
  const n = count ? formatCountForLocale(count, 'en') : null;
  return {
    title: clampTitleClean(n ? `Companies in Finland: ${n} Hiring Now | JobCrawls` : 'Companies in Finland: Who Is Hiring Now | JobCrawls'),
    description: clampMetaDescription(
      'Finnish companies ranked by staff size, plus who is hiring now: open jobs, pay bands and remote share for companies in Finland. Browse by city or industry.',
    ),
    keywords:
      'companies in Finland, Finnish companies, international companies in Finland, top companies in Finland, largest employers Finland, JobCrawls',
    ogLocale: 'en_US',
  };
}

function formatCountForLocale(count: number, locale: BestCompaniesLocale): string {
  // Finnish and Swedish group thousands with a space; English with a comma.
  return locale === 'en' ? count.toLocaleString('en-US') : count.toLocaleString('fi-FI').replace(/\u00a0/g, ' ');
}

function dimensionDisplayLabel(locale: BestCompaniesLocale, config: DimensionConfig, dimensionSlug: string): string {
  return getBestCompaniesDimensionDisplayLabel(dimensionSlug, locale) ?? config.label;
}

/**
 * Visible H1 for a dimension page, aligned with the title's search target:
 * city fi "Tampereen suurimmat työnantajat", en "Companies in Tampere", sv "Företag och
 * arbetsgivare i Tammerfors"; industry "IT and software companies in Finland".
 */
export function buildBestCompaniesDimensionHeading(
  locale: BestCompaniesLocale,
  config: DimensionConfig,
  dimensionSlug: string,
): string {
  const label = dimensionDisplayLabel(locale, config, dimensionSlug);
  if (config.type === 'industry') {
    const name = getBestCompaniesIndustrySeoName(dimensionSlug, locale) ?? label;
    return locale === 'fi' ? `${name} Suomessa` : locale === 'sv' ? `${name} i Finland` : `${name} in Finland`;
  }
  if (locale === 'fi') {
    if (config.type === 'city') {
      // "{city} yritykset" is what people search ("oulu yritykset" 100/mo, "yritykset tampere"
      // 80/mo, Ahrefs FI Oct 2026); "{city} suurimmat työnantajat" has no measurable volume.
      const gen = getCityGenitiveOrNull(label);
      if (gen) return `${gen} yritykset ja suurimmat työnantajat`;
      return `Yritykset ja työnantajat ${getCityInessiveOrNull(label) ?? label}`;
    }
    const gen = getBestCompaniesRegionFiForms(dimensionSlug)?.genitive;
    return gen ? `${gen} yritykset ja suurimmat työnantajat` : `Yritykset ja työnantajat: ${label}`;
  }
  if (locale === 'sv') return `Företag och arbetsgivare i ${label}`;
  return `Companies in ${label}`;
}

/**
 * Short phrase for use inside a sentence such as "Who's hiring anyone in {X}?":
 * city en "Tampere", fi "Tampereella", sv "Tammerfors"; industry en "IT and software",
 * fi "IT- ja ohjelmistoalalla", sv "inom IT och mjukvara".
 */
export function buildBestCompaniesDimensionScopeLabel(
  locale: BestCompaniesLocale,
  config: DimensionConfig,
  dimensionSlug: string,
): string {
  const label = dimensionDisplayLabel(locale, config, dimensionSlug);
  if (config.type === 'industry') {
    return getBestCompaniesIndustryScopePhrase(dimensionSlug, locale) ?? label;
  }
  if (locale === 'fi') {
    if (config.type === 'city') return getCityInessiveOrNull(label) ?? label;
    return getBestCompaniesRegionFiForms(dimensionSlug)?.scope ?? label;
  }
  return label;
}

export function buildBestCompaniesDimensionSeo(
  locale: BestCompaniesLocale,
  config: DimensionConfig,
  dimensionSlug: string
): { title: string; description: string; keywords: string; ogLocale: string } {
  const ogLocale = locale === 'fi' ? 'fi_FI' : locale === 'sv' ? 'sv_SE' : 'en_US';
  const displayLabel = dimensionDisplayLabel(locale, config, dimensionSlug);
  const industryName =
    config.type === 'industry' ? (getBestCompaniesIndustrySeoName(dimensionSlug, locale) ?? displayLabel) : null;

  if (locale === 'fi') {
    const typeWordFi = config.type === 'city' ? 'kaupunki' : config.type === 'region' ? 'alue' : 'toimiala';
    if (config.type === 'industry') {
      const name = industryName as string;
      return {
        title: clampTitleClean(`${name} Suomessa | Rekrytoivat nyt | JobCrawls`),
        description: clampMetaDescription(
          `${name} Suomessa: suurimmat työnantajat henkilöstön mukaan ja ketkä rekrytoivat nyt. Avoimet työpaikat, palkkahaarukat ja etätyön osuus yrityksittäin.`,
        ),
        keywords: `${name.toLowerCase()}, suurimmat työnantajat, rekrytoivat yritykset, avoimet työpaikat, ${typeWordFi}, JobCrawls`,
        ogLocale,
      };
    }
    const genitive =
      config.type === 'city' ? getCityGenitiveOrNull(displayLabel) : (getBestCompaniesRegionFiForms(dimensionSlug)?.genitive ?? null);
    if (genitive) {
      return {
        // Leads with "{city} yritykset", the searched phrase (see buildBestCompaniesDimensionHeading).
        title: clampTitleClean(`${genitive} yritykset ja suurimmat työnantajat | JobCrawls`),
        description: clampMetaDescription(
          `${genitive} yritykset ja suurimmat työnantajat sekä ketkä rekrytoivat nyt: avoimet työpaikat, palkkahaarukat ja etätyön osuus. Perustuu työpaikkailmoituksiin.`,
        ),
        keywords: `${genitive} yritykset, ${displayLabel} yritykset, ${genitive} suurimmat työnantajat, rekrytoivat yritykset, avoimet työpaikat, JobCrawls`,
        ogLocale,
      };
    }
    // No vetted genitive: keep the inessive-based phrasing (nominative as the last resort).
    const scope =
      config.type === 'city' ? (getCityInessiveOrNull(displayLabel) ?? displayLabel) : `: ${displayLabel}`;
    return {
      title: clampTitleClean(
        config.type === 'city' ? `Työnantajat ${scope} | Rekrytoivat nyt | JobCrawls` : `Työnantajat${scope} | Rekrytoivat nyt | JobCrawls`,
      ),
      description: clampMetaDescription(
        config.type === 'city'
          ? `Työnantajat ${scope}, jotka rekrytoivat juuri nyt: avoimet työpaikat, palkkahaarukat ja etätyön osuus. Perustuu työpaikkailmoituksiin, ei käyttäjäarvosteluihin.`
          : `Työnantajat (${displayLabel}), jotka rekrytoivat juuri nyt: avoimet työpaikat, palkkahaarukat ja etätyön osuus. Perustuu työpaikkailmoituksiin, ei käyttäjäarvosteluihin.`,
      ),
      keywords: `työnantajat, ${displayLabel}, avoimet työpaikat, rekrytoivat yritykset, ${typeWordFi}, JobCrawls`,
      ogLocale,
    };
  }
  if (locale === 'sv') {
    const typeWordSv = config.type === 'city' ? 'stad' : config.type === 'region' ? 'region' : 'bransch';
    if (config.type === 'industry') {
      const name = industryName as string;
      return {
        title: clampTitleClean(`${name} i Finland | Rekryterar nu | JobCrawls`),
        description: clampMetaDescription(
          `${name} i Finland: de största arbetsgivarna efter antal anställda och vilka som rekryterar nu. Lediga jobb, lönespann och andel distansarbete per företag.`,
        ),
        keywords: `${name.toLowerCase()}, största arbetsgivare, företag som rekryterar, lediga jobb, ${typeWordSv}, JobCrawls`,
        ogLocale,
      };
    }
    return {
      title: clampTitleClean(`Företag och arbetsgivare i ${displayLabel} | JobCrawls`),
      description: clampMetaDescription(
        `De största arbetsgivarna och företagen i ${displayLabel} och vilka som rekryterar nu: lediga jobb, lönespann och andel distansarbete. Baserat på platsannonser.`,
      ),
      keywords: `företag i ${displayLabel}, arbetsgivare ${displayLabel}, största arbetsgivare, lediga jobb, ${typeWordSv}, JobCrawls`,
      ogLocale,
    };
  }

  if (config.type === 'industry') {
    const name = industryName as string;
    return {
      title: clampTitleClean(`${name} in Finland: Who Is Hiring | JobCrawls`),
      description: clampMetaDescription(
        `${name} in Finland: the largest employers by staff and who is hiring now. Open jobs, pay bands and remote share, from live job listings.`,
      ),
      keywords: `${name.toLowerCase()} in Finland, largest employers, companies hiring, open jobs, JobCrawls`,
      ogLocale,
    };
  }
  return {
    title: clampTitleClean(`Companies in ${displayLabel}: Who Is Hiring | JobCrawls`),
    description: clampMetaDescription(
      `Companies in ${displayLabel}: the largest employers by staff and who is hiring now. Open jobs, pay bands and remote share, from live job listings.`,
    ),
    keywords: `companies in ${displayLabel}, largest employers ${displayLabel}, companies hiring, open jobs, ${config.type}, JobCrawls`,
    ogLocale,
  };
}

const PAGE_WORD: Record<BestCompaniesLocale, string> = { en: 'Page', fi: 'Sivu', sv: 'Sida' };

/**
 * Employers-hub pages 2+ are indexable (each lists 48 real employers, and noindex on paginated
 * lists keeps Google from following them to the profiles), so each needs its own title and
 * description. The " (Sivu N)" suffix is the one truncateSeoTitle already preserves.
 */
export function withHubPageTitle(title: string, page: number | undefined, locale: BestCompaniesLocale): string {
  if (!page || page <= 1) return title;
  // "{title} (Sivu N) | JobCrawls"; when that's too long, drop the brand, then the count clause
  // after the colon ("Suomen suurimmat työnantajat: 2 412 rekrytoi nyt" -> "... työnantajat"), so
  // the page number never lands after the brand or on a half-cut phrase.
  const suffix = ` (${PAGE_WORD[locale]} ${page})`;
  const base = title.replace(/\s*\|\s*JobCrawls\s*$/i, '').trim();
  const shortBase = base.includes(':') ? base.slice(0, base.lastIndexOf(':')).trim() : base;
  const candidates = [`${base}${suffix} | JobCrawls`, `${base}${suffix}`, `${shortBase}${suffix} | JobCrawls`, `${shortBase}${suffix}`];
  return candidates.find((c) => c.length <= DEFAULT_SEO_TITLE_MAX_LENGTH) ?? truncateSeoTitle(`${shortBase}${suffix}`);
}

export function withHubPageDescription(description: string, page: number | undefined, locale: BestCompaniesLocale): string {
  if (!page || page <= 1) return description;
  return clampMetaDescription(`${PAGE_WORD[locale]} ${page}. ${description}`);
}
